import React, { useMemo } from 'react';

import { FlagAndName, Error } from '../../atoms';
import { Card, DataCenterSkeleton } from '../../molecules';
import { ComposableList, IComposableListAccessibleItem } from '../../organisms';
import ErrorBoundary from '../error-boundary/error-boundary';
import t from '../../../labels/labelling-utils';
import { IComponent } from '../../../typings/generic-component';
import { makeClassNames } from '../../../utils/styles';
import { objectToDataAttributes } from '../../../utils/tracking-data';
import './card-motorsports-results.scss';

export interface ICardResult extends IComposableListAccessibleItem {
  driverName: string;
  customDriverPageUrl: string;
  flagUrl: string;
  result: string;
  position: string;
}

export interface ICardMotorsportsResults extends IComponent {
  competitionId?: string;
  customCompetitionName?: string;
  customGpName?: string;
  resultsLink?: string;
  isSprint?: boolean;
  results: ICardResult[];
  isLoading?: boolean;
  trackingData?: {};
  cardCtaTrackingData?: {};
}

interface IDCMotorsportsResultsClasses {
  cardResults: string;
  cardResultsWrapper: string;
}

const isValidResults = (results: ICardResult[], isLoading: boolean) => isLoading || Boolean(results && results.length);

const classNames = (): IDCMotorsportsResultsClasses =>
  makeClassNames({
    cardResults: 'card-motorsports-result',
    cardResultsWrapper: 'card-motorsports-result__wrapper',
  }) as IDCMotorsportsResultsClasses;

const CardMotorsportsResults: React.FC<ICardMotorsportsResults> = ({
  customCompetitionName,
  customGpName,
  resultsLink,
  isSprint = false,
  isLoading = false,
  trackingData = {},
  cardCtaTrackingData = {},
  results,
}) => {
  const cn = classNames();
  const hasError = useMemo<boolean>(() => !isValidResults(results, isLoading), [results, isLoading]);

  return (
    <aside
      className={cn.cardResults}
      {...objectToDataAttributes(trackingData)}
      role="document"
      title="Data card risultati"
    >
      <ErrorBoundary hasError={hasError} errorComponent={<Error>{t('unavailable.data')}</Error>}>
        {isLoading ? (
          <DataCenterSkeleton size="small" rowCount={5} />
        ) : (
          <Card
            cta={t('card.view.all')}
            ctaLink={resultsLink}
            title={`Risultati ${customCompetitionName}`}
            subTitle={
              isSprint ? `${t('motorsports.sessions.tabs.SPRINT')} ${customGpName?.slice(3)}` : `${customGpName}`
            }
            size="small"
            ctaMobile
            ctaMobileWhite
            trackingData={cardCtaTrackingData}
          >
            <ComposableList<ICardResult>
              headers={{
                position: 'Pos',
                driverName: 'Piloti',
                result: 'Risultato',
              }}
              items={results}
              fields={[
                'position',
                {
                  prop: 'driverName',
                  render: (data) => <FlagAndName flagUrl={data.flagUrl} name={data.driverName} color="light" />,
                },
                'result',
              ]}
              inverted
              aria-label="risultati"
              aria-description={`Risultati ${customCompetitionName}`}
            />
          </Card>
        )}
      </ErrorBoundary>
    </aside>
  );
};

export default CardMotorsportsResults;
